import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { BookingQuestionsModal } from '../common/modals.js';
import BookingQuestionsForm from './bookingQuestionsForm.js';
import { StandardDateSelector } from '../common/formComponents/StandardDateSelector';
import PolarSwitch from '../common/formComponents/polarSwitch';
import AsyncSelect from 'react-select/async';
import { SEC_ApiCall, SEC_DataApiPost } from '../func.js';
import { FaTrashAlt, FaCheck } from 'react-icons/fa';
import { CgSpinner } from 'react-icons/cg';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './bulkBookingModal.css';

/**
 * Modal used to perform bulk bookings. Allows the user to select how many
 * additional lessons to book or an end date, check availability and then submit
 * all bookings in sequence.
 */
export default function BulkBookingModal({
    modalActive,
    setModalActive,
    groupId,
    roomId,
    lessonId,
    roomName,
    startDate,
    questions = [],
    onComplete,
    bulkAllowed = false,
    proxyAllowed = false
}) {
    const [repeatCount, setRepeatCount] = useState('');
    const [endDate, setEndDate] = useState(null);
    // When false only the toggle is displayed. Switching on reveals bulk booking controls.
    const [bulkEnabled, setBulkEnabled] = useState(false);
    // Determines whether the user repeats by number of lessons or until an end date.
    const [repeatOption, setRepeatOption] = useState('');
    const [availability, setAvailability] = useState([]);
    const [responses, setResponses] = useState({});
    const [submitting, setSubmitting] = useState(false);
    // After a successful submission the button text changes to "Close"
    const [submitted, setSubmitted] = useState(false);
    const [booked, setBooked] = useState([]);
    const [formKey, setFormKey] = useState(0);
    // Track pending availability requests so the button can show a spinner
    // while still allowing repeated checks.
    const [pendingChecks, setPendingChecks] = useState(0);
    const latestCheck = useRef(0);
    const [proxyEnabled, setProxyEnabled] = useState(false);
    const [proxyUser, setProxyUser] = useState(null);
    const [proxyOptions, setProxyOptions] = useState([]);
    const [proxyLoading, setProxyLoading] = useState(false);

    // Format date and lesson strings for display at the top of the modal
    const displayDate = startDate ? moment(startDate, 'DDMMYYYY').format('dddd DD/MM/YYYY') : '';
    const displayLesson = lessonId ? lessonId.replace(/_/g, ' ') : '';

    // Retrieve proxy booking options from the API based on user input.
    // When ``input`` is blank the backend should return a default set of staff.
    const loadProxyOptions = async (input = '') => {
        try {
            const params = input ? { q: input } : {};
            const res = await SEC_ApiCall('bookings/proxy/eligible-staff', params);
            return (res.items || []).map(i => ({ value: i.cogId, label: i.displayName }));
        } catch (e) {
            console.error('failed to load proxy staff', e);
            return [];
        }
    };

    // When the proxy toggle is enabled fetch an initial list of staff. If the
    // API returns no results or fails, notify the user and disable proxy mode.
    // While the request is in progress the switch displays a spinner.
    useEffect(() => {
        if (proxyEnabled) {
            setProxyLoading(true);
            (async () => {
                try {
                    const opts = await loadProxyOptions('');
                    if (opts.length === 0) {
                        console.error('no proxy staff returned');
                        toast.error('An error has occurred getting available staff');
                        setProxyEnabled(false);
                    } else {
                        setProxyOptions(opts);
                    }
                } catch (err) {
                    console.error('error loading proxy staff', err);
                    toast.error('An error has occurred getting available staff');
                    setProxyEnabled(false);
                } finally {
                    setProxyLoading(false);
                }
            })();
        } else {
            setProxyOptions([]);
            setProxyUser(null);
            setProxyLoading(false);
        }
    }, [proxyEnabled]);

    const checkAvailability = async () => {
        // Clear previous results so the list resets for each check.
        setAvailability([]);
        setBooked([]);

        const payload = {
            group: groupId,
            room: roomId,
            lesson: lessonId,
            date: startDate,
        };
        if (repeatOption === 'count' && repeatCount) payload.repeatCount = Number(repeatCount);
        if (repeatOption === 'date' && endDate) payload.endDate = moment(endDate).format('DDMMYYYY');

        // Each request increments a counter so multiple checks can run concurrently.
        const thisCheck = ++latestCheck.current;
        setPendingChecks(prev => prev + 1);
        try {
            const result = await SEC_DataApiPost(
                'bookings/CheckBulkAvailability',
                JSON.stringify(payload),
                {},
                () => {}
            );
            if (thisCheck === latestCheck.current) {
                if (Array.isArray(result)) {
                    setAvailability(result);
                } else if (result && result.data) {
                    setAvailability(result.data);
                } else {
                    setAvailability([]);
                }
            }
        } catch (e) {
            console.log('availability check failed', e);
            if (thisCheck === latestCheck.current) {
                setAvailability([]);
            }
        } finally {
            setPendingChecks(prev => Math.max(prev - 1, 0));
        }
    };

    // Store the latest responses from the booking questions form.
    const handleQuestionChange = (resp) => {
        setResponses(resp);
    };

    const removeSlot = (idx) => {
        setAvailability(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSubmit = async () => {
        // Require a proxy selection when booking on behalf of someone else.
        if (proxyEnabled && !proxyUser) {
            toast.error('⚠️ Please select a user to book for.', { autoClose: 4000, position: 'top-right' });
            return;
        }

        // Bulk booking cannot be combined with proxy booking at present.
        if (bulkEnabled && proxyEnabled) {
            toast.error('⚠️ Proxy booking is not available when bulk booking.', { autoClose: 4000, position: 'top-right' });
            return;
        }

        // Validate bulk booking selections before allowing submission.
        if (bulkEnabled) {
            if (!repeatOption) {
                toast.error('⚠️ Please select a repeat option.', { autoClose: 4000, position: 'top-right' });
                return;
            }
            if (repeatOption === 'count' && !repeatCount) {
                toast.error('⚠️ Please enter the number of lessons.', { autoClose: 4000, position: 'top-right' });
                return;
            }
            if (repeatOption === 'date' && !endDate) {
                toast.error('⚠️ Please choose an end date.', { autoClose: 4000, position: 'top-right' });
                return;
            }
            if (availability.length === 0) {
                toast.error('⚠️ Please check availability before submitting.', { autoClose: 4000, position: 'top-right' });
                return;
            }
        }

        setSubmitting(true);
        const respString = JSON.stringify(responses);

        if (!bulkEnabled) {
            // Single booking mode – call the standard booking API.
            const payload = {
                type: 'booking',
                group: groupId,
                date: startDate,
                room: roomId,
                lesson: lessonId,
                questionsResponse: respString
            };
            if (proxyEnabled && proxyUser) payload.proxyFor = proxyUser.value;
            try {
                const resp = await SEC_DataApiPost('bookings', JSON.stringify(payload));
                if (resp && resp.data === 'success') {
                    const day = moment(startDate, 'DDMMYYYY').format('dddd');
                    const formatted = moment(startDate, 'DDMMYYYY').format('DD/MM/YYYY');
                    toast.success(`✅ Booking confirmed for room ${roomName} on ${lessonId.replace(/_/g, ' ')} ${day} ${formatted}`, { autoClose: 4000, position: 'top-right' });
                    if (onComplete) {
                        await onComplete({
                            mode: 'single',
                            slot: {
                                roomId,
                                lessonId,
                                date: startDate
                            }
                        });
                    }
                    setModalActive(false);
                } else {
                    toast.error('❌ Booking request failed', { autoClose: 4000, position: 'top-right' });
                }
            } catch (e) {
                console.error('booking failed', e);
                toast.error('❌ Booking request failed', { autoClose: 4000, position: 'top-right' });
            }
            setSubmitting(false);
            return;
        }

        // Bulk booking mode - only attempt to book slots that remain available.
        const toBook = availability.filter(slot => slot.available).map(s => ({ date: s.date }));
        if (toBook.length === 0) {
            toast.error('⚠️ No available lessons to book.', { autoClose: 4000, position: 'top-right' });
            setSubmitting(false);
            return;
        }

        const bulkPayload = {
            group: groupId,
            room: roomId,
            lesson: lessonId,
            occurrences: toBook,
            questionsResponse: respString,
            origin: 'UI',
            seriesMeta: {}
        };
        if (repeatOption === 'count') {
            bulkPayload.seriesMeta.scope = { mode: 'count', count: Number(repeatCount) };
        } else if (repeatOption === 'date') {
            bulkPayload.seriesMeta.scope = { mode: 'endDate', endDate: moment(endDate).format('DDMMYYYY') };
        }

        try {
            const resp = await SEC_DataApiPost('bookings/BulkBooking', JSON.stringify(bulkPayload));
            const bookedDates = (resp.results || []).filter(r => r.ok).map(r => r.date);
            setBooked(bookedDates);
            if (resp.stats) {
                const { requested, booked } = resp.stats;
                toast.success(`✅ Bulk booking complete. ${booked} of ${requested} lessons booked`, { autoClose: 4000, position: 'top-right' });
            } else {
                toast.success('✅ Bulk booking complete', { autoClose: 4000, position: 'top-right' });
            }
            if (onComplete) {
                await onComplete({ mode: 'bulk' });
            }
        } catch (e) {
            console.error('bulk booking failed', e);
            toast.error('❌ Bulk booking failed', { autoClose: 4000, position: 'top-right' });
            setSubmitting(false);
            return;
        }

        setSubmitting(false);
        setSubmitted(true);
    };

    // Reset state related to bulk booking while leaving proxy selections intact.
    const resetBulkState = () => {
        setBulkEnabled(false);
        setRepeatOption('');
        setRepeatCount('');
        setEndDate(null);
        setAvailability([]);
        setResponses({});
        setSubmitting(false);
        setSubmitted(false);
        setBooked([]);
        setFormKey(prev => prev + 1);
    };

    // Reset the entire modal state including proxy booking options.
    const resetAllState = () => {
        resetBulkState();
        setProxyEnabled(false);
        setProxyUser(null);
        setProxyOptions([]);
        setProxyLoading(false);
    };

    // Toggle the bulk booking controls on or off. When disabling, clear any prior state.
    const handleToggle = (val) => {
        if (val) {
            setBulkEnabled(true);
        } else {
            resetBulkState();
        }
    };

    // Handle changes to the repeat mode selection.
    const handleOptionChange = (val) => {
        setRepeatOption(val);
        setRepeatCount('');
        setEndDate(null);
    };

    useEffect(() => {
        if (!modalActive) {
            resetAllState();
        }
    }, [modalActive]);

    const hasUnavailable = availability.some(slot => !slot.available);

    return (
        <BookingQuestionsModal modalActiveState={modalActive} setStateFunction={setModalActive}>
            <div className='bulkBookingContainer'>
                <div className='bookingModalDetails'>
                    {/* Display booking details for user reference */}
                    {displayDate && <p>{displayDate} - {displayLesson}</p>}
                    {roomName && <p>{roomName}</p>}
                </div>
                <div className='bookingOptions'>
                    {/* Proxy booking toggle and selector */}
                    {proxyAllowed && (
                        <>
                            <div className='optionRow'>
                                <span className='optionLabel'>Book for someone else</span>
                                <PolarSwitch data={proxyEnabled} setData={setProxyEnabled} loading={proxyLoading} />
                            </div>
                            {proxyEnabled && proxyOptions.length > 0 && (
                                <AsyncSelect
                                    cacheOptions
                                    defaultOptions={proxyOptions}
                                    loadOptions={loadProxyOptions}
                                    value={proxyUser}
                                    onChange={setProxyUser}
                                    className='proxySelect'
                                    placeholder='Select staff member'
                                />
                            )}
                        </>
                    )}
                    {/* Bulk booking toggle and controls */}
                    {bulkAllowed && (
                        <>
                            <div className='optionRow'>
                                <span className='optionLabel'>Bulk Book</span>
                                <PolarSwitch data={bulkEnabled} setData={handleToggle} />
                            </div>
                            {bulkEnabled && (
                                <>
                                    <div className='repeatControls'>
                                        <select value={repeatOption} onChange={e => handleOptionChange(e.target.value)}>
                                            <option value=''>Select option</option>
                                            <option value='count'>Number of lessons</option>
                                            <option value='date'>End date</option>
                                        </select>
                                        {repeatOption === 'count' && (
                                            <div className='repeatField'>
                                                <label>Number of lessons</label>
                                                <input type='number' min='1' value={repeatCount} onChange={e => setRepeatCount(e.target.value)} />
                                            </div>
                                        )}
                                        {repeatOption === 'date' && (
                                            <div className='repeatField'>
                                                <StandardDateSelector title='End date' value={endDate} setData={setEndDate} />
                                            </div>
                                        )}
                                        <button
                                            type='button'
                                            className='checkButton'
                                            onClick={checkAvailability}
                                            disabled={
                                                repeatOption === '' ||
                                                (repeatOption === 'count' && !repeatCount) ||
                                                (repeatOption === 'date' && !endDate)
                                            }
                                        >
                                            {pendingChecks > 0 ? (
                                                <CgSpinner className='submit-button-spinner' />
                                            ) : (
                                                'Check availability'
                                            )}
                                        </button>
                                    </div>

                                    {availability.length > 0 && (
                                        <ul className='availabilityList'>
                                            {availability.map((slot, idx) => {
                                                const formatted = moment(slot.date, 'DDMMYYYY').format('DD/MM/YYYY');
                                                return (
                                                    <li key={`${slot.date}-${idx}`}>
                                                        <span className='slotDate'>{formatted}</span>
                                                        <span
                                                            className={`slotStatus ${slot.available ? 'statusAvailable' : 'statusUnavailable'}`}
                                                        >
                                                            {slot.available ? 'Available' : 'Unavailable'}
                                                        </span>
                                                        {booked.includes(slot.date) ? (
                                                            <FaCheck className='bookedIcon' />
                                                        ) : (
                                                            slot.available && (
                                                                <button
                                                                    type='button'
                                                                    className='removeButton'
                                                                    onClick={() => removeSlot(idx)}
                                                                >
                                                                    <FaTrashAlt />
                                                                </button>
                                                            )
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
                <hr className='bulkDivider' />
                {questions.length > 0 && (
                    <BookingQuestionsForm
                        key={formKey}
                        questions={questions}
                        onChange={handleQuestionChange}
                        showSubmit={false}
                    />
                )}
                <div className='submitArea'>
                    {hasUnavailable && (
                        <p className='availabilityNote'>
                            Only available lessons will be booked, Unavailable lessons will be ignored
                        </p>
                    )}
                    <button
                        className='formSubmitButton'
                        disabled={submitted ? false : submitting}
                        type='button'
                        onClick={submitted ? () => setModalActive(false) : handleSubmit}
                    >
                        {submitted ? 'Close' : submitting ? <CgSpinner className='submit-button-spinner' /> : 'Submit'}
                    </button>
                </div>
            </div>
        </BookingQuestionsModal>
    );
}
